import { makeStyles } from '@mui/styles';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { handleErrors } from '../../actions/ErrorActions';
import { getSavedSearchDetails } from '../../api/saved-search';
import { getSearchTreeViewData } from '../../api/searchtreeviewdata';
import Loader from '../../components/CustomLoader';
import EditorErrorPage from '../../components/EditorErrorPage';
import Layout from '../../components/Layout';
import { SMART_INSIGHT } from '../../constants/Urls';
import { useSavedSearchSelector } from '../../custom-hooks';
import { hasFetchError } from '../../form-designer/selectors/common.selectors';
import { setFetchErrorProp } from '../../form-designer/utils/actions/form-manager.actions.utils';
import SavedSearchEditor from '../../saved-search-designer';
import {
  clearSavedSearch,
  loadEntityFields,
  loadSavedSearches,
} from '../../saved-search-designer/actions/editor.actions';
import Details from '../../saved-search-designer/components/Details';
import Header from '../../saved-search-designer/components/Header';
import { SAVED_SEARCH_TEMPLATE } from '../../saved-search-designer/constants/new-saved-search-template';
import { getSavedSearchName } from '../../saved-search-designer/selectors/editor.selectors';
import { preprocessTreeViewData, treeViewDataToOptions } from '../../utils/savedSearchUtils';

const useStyles = makeStyles({
  loadingContainer: {
    position: 'absolute',
    width: '95%',
    height: '80%',
  },
  mainContainer: {
    margin: '0 20px',
  },
});

function SavedSearchEditorPage() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const isFetchError = useSelector(hasFetchError);
  const savedSearchName = useSavedSearchSelector(getSavedSearchName);

  const { id, isNew, entityName, viewName } = qs.parse(location.search) || {};

  useEffect(() => {
    document.title =
      (savedSearchName && `Saved Search Designer / ${savedSearchName}`) || 'Saved Search Designer';
  }, [savedSearchName]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      const [savedSearchResponse, searchTreeViewDataResponse] = await Promise.all([
        !isNew &&
          getSavedSearchDetails({
            id,
            entityName,
          }),
        getSearchTreeViewData(entityName, true),
      ]);

      if (savedSearchResponse?.hasError || searchTreeViewDataResponse?.hasError) {
        const savedSearchResponseErrors = setFetchErrorProp(savedSearchResponse.errors);
        const searchTreeViewDataResponseErrors = setFetchErrorProp(
          searchTreeViewDataResponse.errors
        );

        savedSearchResponse?.hasError && dispatch(handleErrors(savedSearchResponseErrors));
        searchTreeViewDataResponse?.hasError &&
          dispatch(handleErrors(searchTreeViewDataResponseErrors));
        return;
      }

      const searchTreeViewData = searchTreeViewDataResponse?.treeViewData;

      if (!isNew) {
        savedSearchResponse.savedSearch.distribution.distributionList.forEach(
          (item) => (item.rowId = item.id)
        );
      }

      const savedSearchId = isNew ? 'NEW_SAVED_SEARCH' : id;

      const entityTreeFields = preprocessTreeViewData(
        searchTreeViewData ? [searchTreeViewData] : []
      );
      const entityFields = searchTreeViewData ? treeViewDataToOptions(searchTreeViewData) : [];

      dispatch(
        loadSavedSearches({
          savedSearches: {
            [savedSearchId]: {
              savedSearch: isNew
                ? { ...SAVED_SEARCH_TEMPLATE, entityName, viewName }
                : savedSearchResponse.savedSearch,
            },
          },
          selectedSavedSearchId: savedSearchId,
        })
      );
      dispatch(loadEntityFields({ entityFields, entityTreeFields }));
      setIsLoading(false);
    };

    fetchData();

    return () => {
      dispatch(clearSavedSearch());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout dashboardLink={SMART_INSIGHT}>
      {isFetchError ? (
        <EditorErrorPage />
      ) : isLoading ? (
        <div className={classes.loadingContainer}>
          <Loader />
        </div>
      ) : (
        <>
          <Header />
          <div className={classes.mainContainer}>
            <Details />
            <SavedSearchEditor />
          </div>
        </>
      )}
    </Layout>
  );
}

export default SavedSearchEditorPage;
